import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, Filter, ExternalLink, Clock, AlertCircle, CheckCircle, XCircle, User, Calendar, Tag, MessageSquare, FileText, Upload } from 'lucide-react';
import { persistedSupportTickets, SupportTicket } from '../../services/mockData/supportTickets';

// Re-export for backward compatibility
type Ticket = SupportTicket;

interface SupportTicketsProps {
  userRole?: string;
  currentUser?: string;
}

const SupportTickets: React.FC<SupportTicketsProps> = ({
  userRole = 'employee',
  currentUser = 'Current User'
}) => {
  // Load tickets from persisted storage
  const [tickets, setTickets] = useState<Ticket[]>(() => persistedSupportTickets.get());

  // Refresh tickets from storage (useful for keeping in sync)
  const refreshTickets = useCallback(() => {
    setTickets(persistedSupportTickets.get());
  }, []);

  // Initial load from persisted storage
  useEffect(() => {
    refreshTickets();
  }, [refreshTickets]);

  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);

  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    priority: 'Medium' as const,
    category: 'Bug' as const,
    attachments: [] as File[]
  });

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         ticket.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         ticket.submittedBy.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'All' || ticket.status === filterStatus;
    const matchesPriority = filterPriority === 'All' || ticket.priority === filterPriority;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  const handleCreateTicket = () => {
    if (!newTicket.title || !newTicket.description) return;

    // Generate unique ID based on existing tickets
    const existingTickets = persistedSupportTickets.get();
    const maxId = existingTickets.reduce((max, t) => {
      const num = parseInt(t.id.replace('TICK-', ''), 10);
      return num > max ? num : max;
    }, 0);

    const ticket: Ticket = {
      id: `TICK-${String(maxId + 1).padStart(3, '0')}`,
      title: newTicket.title,
      description: newTicket.description,
      priority: newTicket.priority,
      status: 'Open',
      category: newTicket.category,
      submittedBy: currentUser,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      attachments: newTicket.attachments.map(f => f.name),
      comments: []
    };

    // Persist to storage (PE-764: This enables notifications)
    persistedSupportTickets.add(ticket);

    // Update local state from storage
    setTickets(persistedSupportTickets.get());

    setNewTicket({
      title: '',
      description: '',
      priority: 'Medium',
      category: 'Bug',
      attachments: []
    });
    setShowCreateTicket(false);

    // Simulate Linear integration
    if (userRole === 'system_admin') {
      setTimeout(() => {
        // Update in persisted storage
        persistedSupportTickets.update(ticket.id, {
          linearTicketId: `RGL-${Math.floor(Math.random() * 1000) + 100}`,
          linearUrl: `https://linear.app/regal/issue/RGL-${Math.floor(Math.random() * 1000) + 100}`
        });
        // Refresh local state
        setTickets(persistedSupportTickets.get());
      }, 2000);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200';
      case 'High': return 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200';
      case 'Medium': return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
      case 'Low': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      default: return 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Open': return 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200';
      case 'In Progress': return 'bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200';
      case 'Pending': return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200';
      case 'Resolved': return 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200';
      case 'Closed': return 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200';
      default: return 'bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Open': return <AlertCircle className="h-4 w-4" />;
      case 'In Progress': return <Clock className="h-4 w-4" />;
      case 'Resolved': return <CheckCircle className="h-4 w-4" />;
      case 'Closed': return <XCircle className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  if (selectedTicket) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <button
              onClick={() => setSelectedTicket(null)}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-4"
            >
              ← Back to Support Tickets
            </button>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{selectedTicket.title}</h1>
                <p className="text-gray-600 dark:text-gray-400">Ticket ID: {selectedTicket.id}</p>
              </div>
              <div className="flex items-center space-x-3">
                {selectedTicket.linearUrl && (
                  <a
                    href={selectedTicket.linearUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>View in Linear</span>
                  </a>
                )}
                <span className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${getPriorityColor(selectedTicket.priority)}`}>
                  <span>{selectedTicket.priority}</span>
                </span>
                <span className={`flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedTicket.status)}`}>
                  {getStatusIcon(selectedTicket.status)}
                  <span>{selectedTicket.status}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Description</h3>
                <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{selectedTicket.description}</p>
              </div>

              {selectedTicket.attachments.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Attachments</h3>
                  <div className="space-y-2">
                    {selectedTicket.attachments.map((attachment, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm text-blue-600 dark:text-blue-400">
                        <FileText className="h-4 w-4" />
                        <span>{attachment}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Comments</h3>
                <div className="space-y-4">
                  {selectedTicket.comments.map((comment) => (
                    <div key={comment.id} className="border-l-4 border-blue-500 pl-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <User className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                        <span className="font-medium text-gray-900 dark:text-gray-100">{comment.author}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(comment.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300">{comment.content}</p>
                    </div>
                  ))}
                  {selectedTicket.comments.length === 0 && (
                    <p className="text-gray-500 dark:text-gray-400 italic">No comments yet.</p>
                  )}
                </div>

                {userRole === 'system_admin' && (
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                    <textarea
                      placeholder="Add a comment..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button className="mt-3 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                      Add Comment
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Ticket Details</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Category:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{selectedTicket.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Submitted by:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{selectedTicket.submittedBy}</span>
                  </div>
                  {selectedTicket.assignedTo && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Assigned to:</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedTicket.assignedTo}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Created:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {new Date(selectedTicket.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Updated:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {new Date(selectedTicket.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                  {selectedTicket.linearTicketId && (
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Linear ID:</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedTicket.linearTicketId}</span>
                    </div>
                  )}
                </div>
              </div>

              {userRole === 'system_admin' && (
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Admin Actions</h3>
                  <div className="space-y-3">
                    <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value={selectedTicket.status}>{selectedTicket.status}</option>
                      <option>Open</option>
                      <option>In Progress</option>
                      <option>Pending</option>
                      <option>Resolved</option>
                      <option>Closed</option>
                    </select>
                    <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option>Assign to...</option>
                      <option>System Admin</option>
                      <option>Mike Wilson</option>
                      <option>Engineering Team</option>
                    </select>
                    <button className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700">
                      Create Linear Ticket
                    </button>
                    <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                      Update Ticket
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Support Tickets</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Submit and track issues, feature requests, and system problems.
            </p>
          </div>
          <button
            onClick={() => setShowCreateTicket(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <Plus className="h-5 w-5" />
            <span>New Ticket</span>
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <Search className="h-5 w-5 absolute left-3 top-3 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search tickets..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 pr-4 py-2 w-80 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option>All</option>
                  <option>Open</option>
                  <option>In Progress</option>
                  <option>Pending</option>
                  <option>Resolved</option>
                  <option>Closed</option>
                </select>
                <select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option>All</option>
                  <option>Critical</option>
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </select>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {filteredTickets.map((ticket) => (
              <div
                key={ticket.id}
                onClick={() => setSelectedTicket(ticket)}
                className="p-6 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{ticket.title}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                      <span className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket.status)}`}>
                        {getStatusIcon(ticket.status)}
                        <span>{ticket.status}</span>
                      </span>
                      {ticket.linearTicketId && (
                        <span className="flex items-center space-x-1 px-2 py-1 bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 rounded-full text-xs font-medium">
                          <ExternalLink className="h-3 w-3" />
                          <span>{ticket.linearTicketId}</span>
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">{ticket.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                      <span className="flex items-center space-x-1">
                        <User className="h-4 w-4" />
                        <span>{ticket.submittedBy}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Tag className="h-4 w-4" />
                        <span>{ticket.category}</span>
                      </span>
                      <span className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                      </span>
                      {ticket.comments.length > 0 && (
                        <span className="flex items-center space-x-1">
                          <MessageSquare className="h-4 w-4" />
                          <span>{ticket.comments.length}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredTickets.length === 0 && (
            <div className="p-12 text-center">
              <AlertCircle className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No tickets found</h3>
              <p className="text-gray-600 dark:text-gray-400">
                {searchQuery || filterStatus !== 'All' || filterPriority !== 'All'
                  ? 'Try adjusting your search or filters.'
                  : 'Create your first support ticket to get started.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {showCreateTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Create Support Ticket</h2>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={newTicket.title}
                  onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description of the issue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description *
                </label>
                <textarea
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Detailed description of the issue, including steps to reproduce"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Priority
                  </label>
                  <select
                    value={newTicket.priority}
                    onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option>Low</option>
                    <option>Medium</option>
                    <option>High</option>
                    <option>Critical</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    value={newTicket.category}
                    onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value as any })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option>Bug</option>
                    <option>Feature Request</option>
                    <option>System Issue</option>
                    <option>User Access</option>
                    <option>Performance</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Attachments
                </label>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md p-6 text-center">
                  <Upload className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Drop files here or click to upload
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowCreateTicket(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
                <button
                  onClick={handleCreateTicket}
                  disabled={!newTicket.title || !newTicket.description}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title={!newTicket.title ? 'Title is required' : !newTicket.description ? 'Description is required' : 'Create ticket'}
                >
                  Create Ticket
                </button>
                {(!newTicket.title || !newTicket.description) && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {!newTicket.title && !newTicket.description 
                      ? 'Title and description are required'
                      : !newTicket.title 
                      ? 'Title is required'
                      : 'Description is required'}
                  </p>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;